import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowUpRight } from 'react-icons/fi';
import { motion } from 'framer-motion';
import './Header.css';

const Header = () => {
  const videoRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    const handleLoaded = () => {
      setVideoLoaded(true);
      video.play().catch(() => {});
    };
    video.addEventListener('loadedmetadata', handleLoaded);

    const tryPlay = () => {
      video.play().catch(() => {});
      document.removeEventListener('click', tryPlay);
      document.removeEventListener('scroll', tryPlay);
      document.removeEventListener('touchstart', tryPlay);
    };
    document.addEventListener('click', tryPlay);
    document.addEventListener('scroll', tryPlay);
    document.addEventListener('touchstart', tryPlay);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoaded);
      document.removeEventListener('click', tryPlay);
      document.removeEventListener('scroll', tryPlay);
      document.removeEventListener('touchstart', tryPlay);
    };
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.3 } }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { type: 'spring', stiffness: 100, damping: 20 } }
  };

  const highlightVariants = {
    hidden: { scaleX: 0, originX: 0 },
    visible: { scaleX: 1, transition: { delay: 0.8, duration: 1.2, ease: [0.22, 1, 0.36, 1] } }
  };

  return (
    <header className="hero" role="banner">
      <div className="hero__media" aria-hidden="true">
        <video
          ref={videoRef}
          className={`hero__video ${videoLoaded ? 'loaded' : ''}`}
          src="/video-header.mp4"
          poster="/images/hero-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
        >
          <source src="/videos/hero.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="hero__overlay" />
      </div>

      <div className="hero__container">
        <motion.div className="hero__content" initial="hidden" animate="visible" variants={containerVariants}>
          <motion.div className="hero__badge" variants={itemVariants}>
            <div className="hero__badge-sparkle"></div>
            <span>Innovative Solutions</span>
            <div className="hero__badge-glow"></div>
          </motion.div>

          <motion.h1 className="hero__title" variants={itemVariants} transition={{ delay: 0.2 }}>
            <span className="hero__title-line">PARTNERSHIP MATTERS, BECAUSE</span>
            <span className="hero__title-line hero__title-line--highlight">
              TOGETHER WE ACHIEVE MORE
              <motion.span className="hero__highlight" variants={highlightVariants} />
            </span>
          </motion.h1>

          <motion.p className="hero__subtitle" variants={itemVariants} transition={{ delay: 0.4 }}>
            MNG Construction LLC is dedicated to being a reliable partner in heavy civil contracting. We prioritize
            safety, quality, efficiency, and respect for our customers, subcontractors, suppliers, and the public.
          </motion.p>

          <motion.div className="hero__cta" variants={itemVariants} transition={{ delay: 0.6 }}>
            <div className="hero__cta-group">
              <Link to="/contact" className="btn-pill" aria-label="Start Your Project">
                <span className="btn-pill__text">Start Your Project</span>
           
                <span className="btn-pill__hover"></span>
              </Link>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {!isMobile && (
        <motion.div
          className="hero__scroll-indicator"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
        >
          <div className="scroll-indicator__line">
            <div className="scroll-indicator__dot"></div>
          </div>
          <span>Explore Our Work</span>
        </motion.div>
      )}
    </header>
  );
};

export default Header;
